import { useState, useEffect } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { DaySelector } from './DaysOfTheWeekInput';
import { TimeInput } from './TimeInput';
import { StartsAtStopsAt } from './StartsAtStopsAt';
import { useLockBodyScroll } from './useLockBodyScroll';

const animatedComponents = makeAnimated();

export default function EventModal({
  event,
  langUrl,
  isOpen,
  onClose,
  onSave,
  onDelete,
  demoMode = false,
  trads,
  getDayName,
}) {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [days, setDays] = useState([]);
  const [time, setTime] = useState(undefined);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [startsAt, setStartsAt] = useState(null);
  const [stopsAt, setStopsAt] = useState(null);

  const t = trads?.index?.eventModal || {};
  const tContacts = trads?.contacts || {};
  const modalTitle = event?.id
    ? t.editTitle || t.title || 'Edit event'
    : t.createTitle || 'Add event';

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (event) {
      setName(event.name || '');
      setContent(event.content || '');
      setDays(event.days || []);
      setStartsAt(event.startsAt || null);
      setStopsAt(event.stopsAt || null);
      setSelectedGroupIds(event.groupIds || []);
      // Set selected contacts from event.eventContacts
      const selected = (event.eventContacts || []).map((ec) => ({
        value: ec.contactId || ec.contact?.id,
        label: ec.contact?.name || ec.name,
        verified: ec.contact?.verified ?? ec.verified,
        phoneNumber: ec.contact?.phoneNumber,
      }));
      setSelectedContacts(selected);
    }
  }, [event]);

  useEffect(() => {
    if (isOpen && !demoMode) {
      setLoadingContacts(true);
      fetch('/api/contacts/get-contacts')
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.contacts) {
            setContacts(data.contacts);
            setGroups(data.groups || []);
            if (data.contacts.length === 1) {
              setSelectedContacts((prev) =>
                prev.length === 0
                  ? [
                      {
                        value: data.contacts[0].id,
                        label: data.contacts[0].name,
                      },
                    ]
                  : prev
              );
            }
          }
        })
        .catch((err) => console.error('Failed to fetch contacts:', err))
        .finally(() => setLoadingContacts(false));
    }
  }, [isOpen, demoMode]);

  const contactOptions = contacts.map((c) => ({
    value: c.id,
    label: c.name,
  }));
  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: `${group.name} (${group.count})`,
  }));
  const selectedGroupOptions = groupOptions.filter((group) =>
    selectedGroupIds.includes(group.value)
  );

  // VERIFICATION STYLING DISABLED - Keeping code for future use
  /*
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.data.verified ? '#111' : '#111',
      backgroundColor: state.isFocused ? '#white' : 'white',
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.verified ? '#dcfce7' : '#ffedd5',
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: state.data.verified ? '#16a34a' : '#ea580c',
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: state.data.verified ? '#16a34a' : '#ea580c',
      ':hover': {
        backgroundColor: state.data.verified ? '#bbf7d0' : '#fed7aa',
        color: state.data.verified ? '#15803d' : '#c2410c',
      },
    }),
  };
  */

  if (!isOpen || !event) return null;

  const handleSave = () => {
    onSave?.({
      ...event,
      name,
      content,
      days,
      startsAt,
      stopsAt,
      times: time ? [timeStringToMinutes(time)] : event.times,
      eventContacts: selectedContacts.map((sc) => ({ contactId: sc.value })),
      groupIds: selectedGroupIds,
    });
    onClose();
  };

  const handleDelete = () => {
    if (onDelete && event.id) {
      // Optional: Add confirmation? For now direct delete as per usual UX in fast prototyping
      onDelete(event.id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">{modalTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="modal-close-btn"
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <input
              type="text"
              value={name}
              placeholder={t.name}
              onChange={(e) => setName(e.target.value)}
              data-testid="event-name-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.smsContent || 'SMS text'}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onClick={() => {}}
              rows={4}
              data-testid="event-content-input"
              data-umami-event="click sms text textarea"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.days || 'Days'}
            </label>
            <DaySelector
              onChange={setDays}
              daysOfTheWeek={days}
              getDayName={getDayName}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.time || 'Time'}
            </label>
            <TimeInput
              trads={trads}
              onChange={setTime}
              defaultValue={event.times?.[0]}
            />
          </div>

          <StartsAtStopsAt
            startsAt={startsAt}
            stopsAt={stopsAt}
            setStartsAt={setStartsAt}
            setStopsAt={setStopsAt}
            trads={trads}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
              {t.contacts || 'Contacts'}
            </label>
            {demoMode ? (
              <div>
                {selectedContacts.length > 0 ? (
                  <div className="space-y-2">
                    {selectedContacts.map((contact) => (
                      <div
                        key={contact.value}
                        className="px-3 py-2 bg-green-50 border border-green-200 rounded-md text-green-800"
                      >
                        <div className="font-medium">{contact.label}</div>
                        {contact.phoneNumber === '+1564544444' && (
                          <div className="text-xs text-orange-600 mt-1">
                            ⚠️ This is a demo contact that can't be edited,
                            please sign up
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-500 text-sm">
                    No contacts selected
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="contacts-select">
                <Select
                  closeMenuOnSelect={false}
                  components={animatedComponents}
                  isMulti
                  options={contactOptions}
                  value={selectedContacts}
                  onChange={setSelectedContacts}
                  isLoading={loadingContacts}
                  placeholder={t.selectContacts || 'Select contacts...'}
                  noOptionsMessage={() => tContacts.noContacts || 'No contacts'}
                  classNamePrefix="contacts-select"
                />
              </div>
            )}
            {contactOptions.length === 0 && (
              <div className="py-2 text-sm">
                <a
                  className="font-medium text-gray-900 underline hover:text-gray-700"
                  href={`/${langUrl}/contacts`}
                >
                  {t.addContacts}
                </a>
              </div>
            )}
          </div>

          {!demoMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                {t.groups || 'Groups'}
              </label>
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={groupOptions}
                value={selectedGroupOptions}
                onChange={(values) =>
                  setSelectedGroupIds(
                    (values || []).map((group) => group.value)
                  )
                }
                placeholder={t.selectGroups || 'Select groups...'}
                noOptionsMessage={() => tContacts.noGroups || 'No groups'}
                data-testid="event-groups-select"
                classNamePrefix="contacts-select"
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-between gap-3">
          {onDelete && event.id ? (
            <button
              type="button"
              onClick={handleDelete}
              data-testid="event-delete-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              {tContacts.delete || 'Delete'}
            </button>
          ) : (
            <div></div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              data-testid="event-cancel-btn"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              {t.cancel || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={content.trim().length < 3}
              data-testid="event-save-btn"
              data-umami-event="click save event"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.save || 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function timeStringToMinutes(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}
